// src/components/LazyImage.js
// 지연 로딩 이미지 컴포넌트 - 성능 최적화

import React, { useState, useRef, useEffect } from 'react';

const LazyImage = ({ 
  src, 
  alt, 
  className, 
  placeholder = '/placeholder.png',
  onLoad,
  onError,
  fetchPriority = 'low',
  loading = 'lazy',
  decoding = 'async',
  isFirstCard = false,
  isSecondCard = false,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    // [안전성] IntersectionObserver 지원 확인
    if (!window.IntersectionObserver) {
      // 지원하지 않는 브라우저에서는 즉시 로드
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px 0px' } // [최적화] 화면 진입 직전에 미리 로드
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      img.onload = () => {
        setImageSrc(src);
        setIsLoaded(true);
        if (onLoad) onLoad();
      };
      img.onerror = () => {
        if (onError) onError();
      };
      img.src = src;
    }
  }, [isInView, src, onLoad, onError]);

  // 🚀 [LCP 최적화] 첫 번째/두 번째 카드 우선순위 조정
  const getOptimizedProps = () => {
    if (isFirstCard) {
      return {
        fetchPriority: 'high',
        loading: 'eager',
        decoding: 'async'
      };
    } else if (isSecondCard) {
      return {
        fetchPriority: 'high',
        loading: 'eager',
        decoding: 'async'
      };
    } else {
      return {
        fetchPriority: 'low',
        loading: 'lazy',
        decoding: 'async'
      };
    }
  };

  const optimizedProps = getOptimizedProps();

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`${className} ${isLoaded ? 'loaded' : 'loading'}`}
      loading={optimizedProps.loading}
      fetchpriority={optimizedProps.fetchPriority}  // ✅ 소문자로 수정 (React 18+ 규칙)
      decoding={optimizedProps.decoding}
      // [🔧 수정] LCP 이미지 최적화 - preload와 일치하는 속성 추가
      crossOrigin="anonymous"
      {...props}
    />
  );
};

export default LazyImage;
